import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import type { Team, UpdateTeamRequest } from '@kangaroo/shared';

type TeamInfoForm = Record<keyof UpdateTeamRequest, string>;

interface TeamInfoDialogProps {
  open: boolean;
  team: Team;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: UpdateTeamRequest) => Promise<void>;
}

const requiredFields: Array<keyof UpdateTeamRequest> = [
  'name',
  'slug',
  'phone',
  'email',
  'contactName',
  'contactEmail',
  'contactPhone',
  'primaryLocation',
  'addressLine1',
  'city',
  'country',
  'state',
  'postalCode',
  'timezone',
];

function formValue(value: string | null | undefined) {
  return value && value.trim().toLowerCase() !== 'not set' ? value : '';
}

function teamToForm(team: Team): TeamInfoForm {
  return {
    name: formValue(team.name),
    slug: formValue(team.slug),
    phone: formValue(team.phone),
    email: formValue(team.email),
    website: formValue(team.website),
    contactName: formValue(team.contactName),
    contactEmail: formValue(team.contactEmail),
    contactPhone: formValue(team.contactPhone),
    primaryLocation: formValue(team.primaryLocation),
    addressLine1: formValue(team.addressLine1),
    addressLine2: formValue(team.addressLine2),
    city: formValue(team.city),
    country: formValue(team.country),
    state: formValue(team.state),
    postalCode: formValue(team.postalCode),
    timezone: formValue(team.timezone) || 'UTC',
  };
}

function formToPayload(form: TeamInfoForm): UpdateTeamRequest {
  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    website: form.website.trim() || null,
    contactName: form.contactName.trim(),
    contactEmail: form.contactEmail.trim(),
    contactPhone: form.contactPhone.trim(),
    primaryLocation: form.primaryLocation.trim(),
    addressLine1: form.addressLine1.trim(),
    addressLine2: form.addressLine2.trim() || null,
    city: form.city.trim(),
    country: form.country.trim(),
    state: form.state.trim(),
    postalCode: form.postalCode.trim(),
    timezone: form.timezone.trim(),
  };
}

function looksLikeEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function TeamInfoDialog({ open, team, saving, error, onClose, onSubmit }: TeamInfoDialogProps) {
  const [form, setForm] = useState<TeamInfoForm>(() => teamToForm(team));
  const [clientError, setClientError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(teamToForm(team));
      setClientError(null);
    }
  }, [open, team]);

  function updateField(field: keyof TeamInfoForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setClientError(null);

    const missingField = requiredFields.find((field) => !form[field].trim());
    if (missingField) {
      setClientError('Complete all required team info fields.');
      return;
    }

    if (!looksLikeEmail(form.email) || !looksLikeEmail(form.contactEmail)) {
      setClientError('Enter valid team and primary contact email addresses.');
      return;
    }

    await onSubmit(formToPayload(form));
  }

  const formError = clientError ?? error;

  if (!open) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 620, lg: 680 },
          maxWidth: '100vw',
          bgcolor: 'background.default',
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider', px: { xs: 1.1, sm: 1.4 }, py: 1.05 }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1.2, minHeight: 40 }}>
            <Typography sx={{ fontSize: '1.02rem', fontWeight: 950, lineHeight: 1.2 }}>Edit Team Info</Typography>
            <IconButton
              aria-label="Close drawer"
              size="small"
              onClick={onClose}
              disabled={saving}
              sx={{ width: 32, height: 32, color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 0.8 }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1.5, sm: 2 }, py: 2 }}>
          <Stack spacing={2.5}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'text.secondary', mb: 1 }}>
                Team Identity
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 1.5 }}>
                <TextField label="Team name" value={form.name} onChange={(event) => updateField('name', event.target.value)} required size="small" />
                <TextField label="Team code" value={team.code} disabled size="small" helperText="Set during creation" />
                <TextField label="Slug" value={form.slug} onChange={(event) => updateField('slug', event.target.value.toLowerCase())} required size="small" helperText="lowercase-hyphen" />
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'text.secondary', mb: 1 }}>
                Public Contact
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                <TextField label="Team phone" value={form.phone} onChange={(event) => updateField('phone', event.target.value)} required size="small" />
                <TextField label="Team email" type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} required size="small" />
                <TextField label="Website" type="url" value={form.website} onChange={(event) => updateField('website', event.target.value)} size="small" placeholder="https://" />
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'text.secondary', mb: 1 }}>
                Primary Contact
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                <TextField label="Contact name" value={form.contactName} onChange={(event) => updateField('contactName', event.target.value)} required size="small" />
                <TextField label="Contact email" type="email" value={form.contactEmail} onChange={(event) => updateField('contactEmail', event.target.value)} required size="small" />
                <TextField label="Contact phone" value={form.contactPhone} onChange={(event) => updateField('contactPhone', event.target.value)} required size="small" />
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'text.secondary', mb: 1 }}>
                Location
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                <TextField label="Primary track / location" value={form.primaryLocation} onChange={(event) => updateField('primaryLocation', event.target.value)} required size="small" />
                <TextField label="Timezone" value={form.timezone} onChange={(event) => updateField('timezone', event.target.value)} required size="small" />
                <TextField label="Address line 1" value={form.addressLine1} onChange={(event) => updateField('addressLine1', event.target.value)} required size="small" />
                <TextField label="Address line 2" value={form.addressLine2} onChange={(event) => updateField('addressLine2', event.target.value)} size="small" />
                <TextField label="City" value={form.city} onChange={(event) => updateField('city', event.target.value)} required size="small" />
                <TextField label="State / Province" value={form.state} onChange={(event) => updateField('state', event.target.value)} required size="small" />
                <TextField label="Postal code" value={form.postalCode} onChange={(event) => updateField('postalCode', event.target.value)} required size="small" />
                <TextField label="Country" value={form.country} onChange={(event) => updateField('country', event.target.value)} required size="small" />
              </Box>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', px: { xs: 1.5, sm: 2 }, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save Team Info'}</Button>
        </Box>
      </Box>
    </Drawer>
  );
}
